import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Pagination,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { club } from 'apis';
import { useTranslation } from 'react-i18next';
import googleMapsToolIcon from 'assets/google-maps-tool-icon.png';
import LeafletMap from '../Components/LeafletMaps/LeafletMap';
import ClubAvatarImage from '../Components/ClubAvatarImage';

interface ClubPublic {
    id: string;
    code?: number;
    title?: string;
    abbreviation?: string;
    founded?: string;
    url?: string;
    email?: string;
    phone1?: string;
    phone2?: string;
    address1?: string;
    regionName?: string;
    regionalUnitName?: string;
    municipalityName?: string;
    latitude?: number | string | null;
    longitude?: number | string | null;
}

interface ClubFilters {
    search: string;
    regionName: string;
    regionalUnitName: string;
    municipalityName: string;
}

interface RegionOption {
    id?: number;
    Id?: number;
    parentId?: number | null;
    ParentId?: number | null;
    name?: string;
    Name?: string;
    description?: string;
    Description?: string;
}

interface NormalizedRegionOption {
    id?: number;
    parentId?: number | null;
    name: string;
}

type DisplayMode = 'list' | 'grid' | 'map';

interface ClubsListState {
    displayMode?: DisplayMode;
    filters?: ClubFilters;
    currentPage?: number;
    scrollTop?: number;
    selectedClubId?: string;
}

const emptyFilters: ClubFilters = {
    search: '',
    regionName: '',
    regionalUnitName: '',
    municipalityName: ''
};

const CLUBS_LIST_STATE_KEY = 'publicClubsListState';

const normalizeText = (value: unknown) =>
    String(value || '')
        .trim()
        .toLowerCase();

const isDisplayMode = (value: unknown): value is DisplayMode => value === 'list' || value === 'grid' || value === 'map';

const getPublicScrollTop = () => {
    if (typeof window === 'undefined') return 0;

    const publicScrollContainer = document.getElementById('public-main-scroll-container');
    return publicScrollContainer?.scrollTop || window.scrollY || 0;
};

const restorePublicScrollTop = (top: number) => {
    window.requestAnimationFrame(() => {
        const publicScrollContainer = document.getElementById('public-main-scroll-container');
        publicScrollContainer?.scrollTo({ top, behavior: 'auto' });
        window.scrollTo({ top, behavior: 'auto' });
    });
};

const getStoredClubsListState = (): ClubsListState | null => {
    if (typeof window === 'undefined') return null;

    try {
        const storedState = JSON.parse(sessionStorage.getItem(CLUBS_LIST_STATE_KEY) || 'null') as ClubsListState | null;
        if (!storedState) return null;

        return {
            displayMode: isDisplayMode(storedState.displayMode) ? storedState.displayMode : undefined,
            filters: {
                ...emptyFilters,
                ...(storedState.filters || {})
            },
            currentPage: Number(storedState.currentPage) > 0 ? Number(storedState.currentPage) : undefined,
            scrollTop: Number(storedState.scrollTop) > 0 ? Number(storedState.scrollTop) : 0,
            selectedClubId: storedState.selectedClubId
        };
    } catch {
        return null;
    }
};

const saveClubsListState = (state: ClubsListState) => {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(CLUBS_LIST_STATE_KEY, JSON.stringify(state));
};

const uniqueOptions = (items: ClubPublic[], field: keyof ClubPublic) =>
    Array.from(new Set(items.map((item) => item[field]).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b));

const getRegionName = (region: RegionOption) => region.name || region.Name || region.description || region.Description || '';

const getRegionId = (region: RegionOption) => region.id ?? region.Id;

const getRegionParentId = (region: RegionOption) => region.parentId ?? region.ParentId ?? null;

const normalizeRegionOption = (region: RegionOption): NormalizedRegionOption | null => {
    const name = getRegionName(region);
    if (!name) return null;

    return {
        id: getRegionId(region),
        parentId: getRegionParentId(region),
        name
    };
};

const uniqueByName = (items: NormalizedRegionOption[]) => {
    const seen = new Set<string>();

    return items.filter((item) => {
        const key = normalizeText(item.name);
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
    });
};

const buildRegionOptions = (regionItems: RegionOption[], clubs: ClubPublic[]) => {
    const normalizedRegions = uniqueByName(regionItems.map(normalizeRegionOption).filter(Boolean) as NormalizedRegionOption[]);
    const parentRegions = normalizedRegions.filter((region) => region.parentId === null || region.parentId === undefined);
    const parentRegionNames = new Set(parentRegions.map((region) => normalizeText(region.name)));
    const fallbackRegions = uniqueOptions(clubs, 'regionName')
        .filter((name) => !parentRegionNames.has(normalizeText(name)))
        .map((name) => ({ name }));

    return [...parentRegions, ...fallbackRegions];
};

const buildRegionalUnitOptions = (regionItems: RegionOption[], clubs: ClubPublic[], selectedRegionName: string) => {
    if (!selectedRegionName) return [];

    const normalizedRegions = uniqueByName(regionItems.map(normalizeRegionOption).filter(Boolean) as NormalizedRegionOption[]);
    const selectedRegion = normalizedRegions.find(
        (region) =>
            (region.parentId === null || region.parentId === undefined) && normalizeText(region.name) === normalizeText(selectedRegionName)
    );
    const childOptions = selectedRegion?.id ? normalizedRegions.filter((region) => region.parentId === selectedRegion.id) : [];
    const childNames = new Set(childOptions.map((region) => normalizeText(region.name)));
    const fallbackUnits = uniqueOptions(
        clubs.filter((clubItem) => normalizeText(clubItem.regionName) === normalizeText(selectedRegionName)),
        'regionalUnitName'
    )
        .filter((name) => !childNames.has(normalizeText(name)))
        .map((name) => ({ name }));

    return [...childOptions, ...fallbackUnits];
};

const buildMunicipalityOptions = (regionItems: RegionOption[], clubs: ClubPublic[], selectedRegionalUnitName: string) => {
    if (!selectedRegionalUnitName) return [];

    const normalizedRegions = uniqueByName(regionItems.map(normalizeRegionOption).filter(Boolean) as NormalizedRegionOption[]);
    const selectedRegionalUnit = normalizedRegions.find(
        (region) =>
            region.parentId !== null &&
            region.parentId !== undefined &&
            normalizeText(region.name) === normalizeText(selectedRegionalUnitName)
    );
    const childOptions = selectedRegionalUnit?.id ? normalizedRegions.filter((region) => region.parentId === selectedRegionalUnit.id) : [];
    const childNames = new Set(childOptions.map((region) => normalizeText(region.name)));
    const fallbackMunicipalities = uniqueOptions(
        clubs.filter((clubItem) => normalizeText(clubItem.regionalUnitName) === normalizeText(selectedRegionalUnitName)),
        'municipalityName'
    )
        .filter((name) => !childNames.has(normalizeText(name)))
        .map((name) => ({ name }));

    return [...childOptions, ...fallbackMunicipalities];
};

const getClubLocation = (clubItem: ClubPublic, fallback: string) =>
    [clubItem.address1, clubItem.regionName, clubItem.regionalUnitName, clubItem.municipalityName].filter(Boolean).join(', ') || fallback;

const hasValidMapCoordinates = (clubItem: ClubPublic) => {
    const lat = clubItem.latitude === null || clubItem.latitude === '' ? NaN : Number(clubItem.latitude);
    const lng = clubItem.longitude === null || clubItem.longitude === '' ? NaN : Number(clubItem.longitude);

    return (
        Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !(lat === 0 && lng === 0)
    );
};

const ITEMS_PER_PAGE = 20;

const scrollToPageTop = () => {
    window.requestAnimationFrame(() => {
        const publicScrollContainer = document.getElementById('public-main-scroll-container');
        publicScrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    });
};

const View = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [initialListState] = useState<ClubsListState | null>(() => getStoredClubsListState());
    const hasMountedFilters = useRef(false);
    const hasRestoredScroll = useRef(false);
    const [displayMode, setDisplayMode] = useState<DisplayMode>(initialListState?.displayMode || 'list');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState<ClubFilters>(initialListState?.filters || emptyFilters);
    const [draftFilters, setDraftFilters] = useState<ClubFilters>(initialListState?.filters || emptyFilters);
    const [currentPage, setCurrentPage] = useState(initialListState?.currentPage || 1);

    const clubsQuery = useQuery(
        'clubs',
        async () => {
            const res = await club.getClubsPublic();
            return Array.isArray(res) ? res : [];
        },
        { suspense: false }
    );

    const regionsQuery = useQuery(
        'club-regions-public',
        async () => {
            const res = await club.getRegionsOfAllClubs();
            return Array.isArray(res) ? res : [];
        },
        { suspense: false }
    );

    const clubs = useMemo(() => (Array.isArray(clubsQuery.data) ? (clubsQuery.data as ClubPublic[]) : []), [clubsQuery.data]);
    const regionItems = useMemo(() => (Array.isArray(regionsQuery.data) ? (regionsQuery.data as RegionOption[]) : []), [regionsQuery.data]);
    const regions = useMemo(() => buildRegionOptions(regionItems, clubs), [regionItems, clubs]);
    const regionalUnits = useMemo(
        () => buildRegionalUnitOptions(regionItems, clubs, draftFilters.regionName),
        [regionItems, clubs, draftFilters.regionName]
    );
    const municipalities = useMemo(
        () => buildMunicipalityOptions(regionItems, clubs, draftFilters.regionalUnitName),
        [regionItems, clubs, draftFilters.regionalUnitName]
    );

    const filteredClubs = useMemo(() => {
        const search = normalizeText(filters.search);
        const regionName = normalizeText(filters.regionName);
        const regionalUnitName = normalizeText(filters.regionalUnitName);
        const municipalityName = normalizeText(filters.municipalityName);

        return clubs.filter((clubItem) => {
            const matchesSearch =
                !search ||
                [
                    clubItem.title,
                    clubItem.abbreviation,
                    clubItem.code,
                    clubItem.address1,
                    clubItem.regionName,
                    clubItem.regionalUnitName,
                    clubItem.municipalityName
                ]
                    .map(normalizeText)
                    .some((value) => value.includes(search));
            const matchesRegion = !regionName || normalizeText(clubItem.regionName) === regionName;
            const matchesRegionalUnit = !regionalUnitName || normalizeText(clubItem.regionalUnitName) === regionalUnitName;
            const matchesMunicipality = !municipalityName || normalizeText(clubItem.municipalityName) === municipalityName;

            return matchesSearch && matchesRegion && matchesRegionalUnit && matchesMunicipality;
        });
    }, [clubs, filters]);

    const activeFilterCount = [filters.search, filters.regionName, filters.regionalUnitName, filters.municipalityName].filter(
        Boolean
    ).length;
    const totalPages = Math.max(1, Math.ceil(filteredClubs.length / ITEMS_PER_PAGE));
    const pageStartIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedClubs = filteredClubs.slice(pageStartIndex, pageStartIndex + ITEMS_PER_PAGE);
    const showingStart = filteredClubs.length ? pageStartIndex + 1 : 0;
    const showingEnd = Math.min(pageStartIndex + paginatedClubs.length, filteredClubs.length);

    useEffect(() => {
        if (!hasMountedFilters.current) {
            hasMountedFilters.current = true;
            return;
        }

        setCurrentPage(1);
    }, [filters, displayMode]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (hasRestoredScroll.current || clubsQuery.isLoading || !initialListState?.scrollTop) return;

        hasRestoredScroll.current = true;
        restorePublicScrollTop(initialListState.scrollTop);
    }, [clubsQuery.isLoading, initialListState]);

    const handleOpenFilters = () => {
        setDraftFilters(filters);
        setIsFilterOpen(true);
    };

    const handleApplyFilters = () => {
        setFilters(draftFilters);
        setIsFilterOpen(false);
    };

    const handleClearFilters = () => {
        setDraftFilters(emptyFilters);
    };

    const handleClubDetails = (clubId: string) => {
        saveClubsListState({
            displayMode,
            filters,
            currentPage,
            scrollTop: getPublicScrollTop(),
            selectedClubId: clubId
        });
        navigate(`manage/${clubId}/details`);
    };

    const handleShowInMap = (clubId: string) => {
        navigate(`/public/clubs/map/${clubId}`);
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        scrollToPageTop();
    };

    const stopCardClick = (event: any) => {
        event.stopPropagation();
    };

    const renderMapIcon = () => (
        <Box component="img" src={googleMapsToolIcon} alt="Google Maps" sx={{ width: 24, height: 24, flexShrink: 0 }} />
    );

    const renderClubImage = (clubItem: ClubPublic, variant: 'list' | 'grid') => (
        <ClubAvatarImage
            clubId={clubItem.id}
            alt={clubItem.title || t('UnnamedClub')}
            iconSize={56}
            sx={{
                width: variant === 'list' ? { xs: '100%', md: 230 } : '100%',
                height: 160,
                minHeight: 160
            }}
        />
    );

    const renderContactBlock = (clubItem: ClubPublic) => {
        const phone = clubItem.phone1 || clubItem.phone2;

        return (
            <Stack spacing={0.5}>
                {phone && (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: '#4d5f70' }}>
                        <PhoneOutlinedIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2">{phone}</Typography>
                    </Stack>
                )}
                {clubItem.email && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <EmailOutlinedIcon sx={{ fontSize: 18, color: '#4d5f70' }} />
                        <Typography
                            component="a"
                            href={`mailto:${clubItem.email}`}
                            onClick={stopCardClick}
                            variant="body2"
                            sx={{ color: '#075aaa', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {clubItem.email}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        );
    };

    const renderLocationBlock = (clubItem: ClubPublic, variant: 'list' | 'grid') => {
        const hasMap = hasValidMapCoordinates(clubItem);

        return (
            <Stack
                direction="row"
                spacing={0.75}
                alignItems="flex-start"
                onClick={(event) => {
                    if (!hasMap) return;
                    stopCardClick(event);
                    handleShowInMap(clubItem.id);
                }}
                sx={{
                    color: variant === 'list' ? '#4d5f70' : 'text.secondary',
                    cursor: hasMap ? 'pointer' : 'default',
                    borderRadius: 1.5,
                    width: 'fit-content',
                    maxWidth: '100%',
                    pr: 1,
                    '&:hover': hasMap ? { background: '#eef7ff', color: '#075aaa' } : undefined
                }}
            >
                <LocationOnOutlinedIcon sx={{ fontSize: variant === 'list' ? 20 : 18, mt: 0.2, flexShrink: 0 }} />
                <Typography variant={variant === 'list' ? 'body1' : 'body2'}>
                    {getClubLocation(clubItem, t('LocationNotAvailable'))}
                </Typography>
                {hasMap && renderMapIcon()}
            </Stack>
        );
    };

    const renderListCard = (clubItem: ClubPublic) => (
        <Box
            key={clubItem.id}
            onClick={() => handleClubDetails(clubItem.id)}
            sx={{
                p: { xs: 2, md: 2.5 },
                border: '1px solid #d9e6f2',
                borderRadius: 3,
                background: '#fff',
                boxShadow: '0 8px 24px rgba(36, 76, 118, 0.08)',
                cursor: 'pointer',
                transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(36, 76, 118, 0.16)',
                    borderColor: '#7db7ef'
                }
            }}
        >
            <Grid container spacing={2.5}>
                <Grid item xs={12} md="auto">
                    {renderClubImage(clubItem, 'list')}
                </Grid>
                <Grid item xs={12} md>
                    <Stack spacing={1.25}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h3" sx={{ color: '#003b73', fontWeight: 800, lineHeight: 1.2 }}>
                                {clubItem.title || t('UnnamedClub')}
                            </Typography>
                            {clubItem.abbreviation && <Chip label={clubItem.abbreviation} size="small" sx={{ fontWeight: 700 }} />}
                        </Stack>
                        {renderLocationBlock(clubItem, 'list')}
                        {renderContactBlock(clubItem)}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );

    const renderImageCard = (clubItem: ClubPublic) => (
        <Grid item xs={12} sm={6} lg={4} key={clubItem.id}>
            <Box
                onClick={() => handleClubDetails(clubItem.id)}
                sx={{
                    p: 1.5,
                    height: '100%',
                    borderRadius: 3,
                    background: '#fff',
                    border: '1px solid #d9e6f2',
                    boxShadow: '0 8px 24px rgba(36, 76, 118, 0.08)',
                    cursor: 'pointer',
                    transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 32px rgba(36, 76, 118, 0.16)',
                        borderColor: '#7db7ef'
                    }
                }}
            >
                {renderClubImage(clubItem, 'grid')}
                <Stack spacing={1} sx={{ pt: 1.5 }}>
                    <Typography variant="h4" sx={{ color: '#003b73', fontWeight: 800 }}>
                        {clubItem.title || t('UnnamedClub')}
                    </Typography>
                    {renderLocationBlock(clubItem, 'grid')}
                    {renderContactBlock(clubItem)}
                    {clubItem.abbreviation && (
                        <Chip label={clubItem.abbreviation} size="small" sx={{ alignSelf: 'flex-start', fontWeight: 700 }} />
                    )}
                </Stack>
            </Box>
        </Grid>
    );

    return (
        <Box sx={{ width: '100%', px: { xs: 2, md: 5 }, py: 3 }}>
            <Stack spacing={2.5} sx={{ maxWidth: 1280, mx: 'auto' }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h2" sx={{ fontWeight: 900, color: '#111827' }}>
                            {t('Clubs')}
                        </Typography>
                        {!clubsQuery.isLoading && displayMode !== 'map' && filteredClubs.length > 0 && (
                            <Typography variant="body2" sx={{ mt: 0.5, color: '#64748b', fontWeight: 600 }}>
                                {t('ShowingRange', { defaultValue: 'Showing {{start}}-{{end}}', start: showingStart, end: showingEnd })}
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={displayMode}
                            onChange={(_, value) => {
                                if (!value) return;
                                setDisplayMode(value);
                            }}
                            sx={{
                                background: '#f4f6f8',
                                borderRadius: 8,
                                p: 0.3,
                                '& .MuiToggleButton-root': {
                                    border: 0,
                                    borderRadius: 8,
                                    px: 2,
                                    textTransform: 'none',
                                    fontWeight: 700
                                },
                                '& .Mui-selected': {
                                    background: '#fff !important',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                                }
                            }}
                        >
                            <ToggleButton value="list">{t('List')}</ToggleButton>
                            <ToggleButton value="grid">{t('Grid')}</ToggleButton>
                            <ToggleButton value="map">{t('Map')}</ToggleButton>
                        </ToggleButtonGroup>
                        <Tooltip title={t('Filters')}>
                            <Button variant="outlined" onClick={handleOpenFilters} sx={{ minWidth: 44, px: 1.2 }}>
                                <FilterAltOutlinedIcon />
                                {activeFilterCount ? (
                                    <Box component="span" sx={{ ml: 0.5, fontWeight: 800 }}>
                                        {activeFilterCount}
                                    </Box>
                                ) : null}
                            </Button>
                        </Tooltip>
                    </Stack>
                </Stack>

                {activeFilterCount > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {filters.regionName && (
                            <Chip
                                label={`${t('ParentFullName')}: ${filters.regionName}`}
                                onDelete={() =>
                                    setFilters((current) => ({ ...current, regionName: '', regionalUnitName: '', municipalityName: '' }))
                                }
                            />
                        )}
                        {filters.regionalUnitName && (
                            <Chip
                                label={`${t('RegionalUnit')}: ${filters.regionalUnitName}`}
                                onDelete={() => setFilters((current) => ({ ...current, regionalUnitName: '', municipalityName: '' }))}
                            />
                        )}
                        {filters.municipalityName && (
                            <Chip
                                label={`${t('Municipality')}: ${filters.municipalityName}`}
                                onDelete={() => setFilters((current) => ({ ...current, municipalityName: '' }))}
                            />
                        )}
                    </Stack>
                )}

                <Divider />

                {clubsQuery.isLoading && (
                    <Stack spacing={2}>
                        {[1, 2, 3].map((item) => (
                            <Skeleton key={item} variant="rounded" height={220} sx={{ borderRadius: 3 }} />
                        ))}
                    </Stack>
                )}

                {!clubsQuery.isLoading && displayMode !== 'map' && filteredClubs.length === 0 && (
                    <Box sx={{ p: 5, textAlign: 'center', borderRadius: 3, background: '#fff', border: '1px solid #e3e8ef' }}>
                        <Typography variant="h3" sx={{ mb: 1 }}>
                            {t('NoClubsOrSchoolsFound', { defaultValue: 'No clubs found' })}
                        </Typography>
                        <Typography color="text.secondary">
                            {t('TryChangingSearchTextOrFilters', { defaultValue: 'Try changing the search text or filters' })}
                        </Typography>
                    </Box>
                )}

                {!clubsQuery.isLoading && displayMode === 'list' && <Stack spacing={2}>{paginatedClubs.map(renderListCard)}</Stack>}

                {!clubsQuery.isLoading && displayMode === 'grid' && (
                    <Grid container spacing={2}>
                        {paginatedClubs.map(renderImageCard)}
                    </Grid>
                )}

                {displayMode === 'map' && (
                    <Box
                        sx={{
                            position: 'relative',
                            height: { xs: 560, md: 640 },
                            overflow: 'hidden',
                            background: '#fff',
                            borderRadius: 3,
                            border: '1px solid #d9e6f2',
                            boxShadow: '0 8px 24px rgba(36, 76, 118, 0.08)'
                        }}
                    >
                        <LeafletMap
                            clubsData={filteredClubs}
                            selectedProvince={filters.municipalityName || filters.regionalUnitName || filters.regionName}
                            isLoading={clubsQuery.isLoading}
                        />
                    </Box>
                )}

                {!clubsQuery.isLoading && displayMode !== 'map' && filteredClubs.length > ITEMS_PER_PAGE && (
                    <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                        <Pagination
                            color="primary"
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            showFirstButton
                            showLastButton
                        />
                    </Stack>
                )}
            </Stack>

            <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{t('Filters')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label={t('Name')}
                            value={draftFilters.search}
                            onChange={(event) => setDraftFilters((current) => ({ ...current, search: event.target.value }))}
                            fullWidth
                        />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: municipalities.length
                                        ? 'repeat(3, minmax(0, 1fr))'
                                        : regionalUnits.length
                                        ? 'repeat(2, minmax(0, 1fr))'
                                        : '1fr'
                                },
                                gap: 2,
                                width: '100%'
                            }}
                        >
                            <Box>
                                <TextField
                                    select
                                    label={t('ParentFullName')}
                                    value={draftFilters.regionName}
                                    onChange={(event) =>
                                        setDraftFilters((current) => ({
                                            ...current,
                                            regionName: event.target.value,
                                            regionalUnitName: '',
                                            municipalityName: ''
                                        }))
                                    }
                                    fullWidth
                                >
                                    {regions.map((region) => (
                                        <MenuItem key={region.name} value={region.name}>
                                            {region.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                            {regionalUnits.length > 0 && (
                                <Box>
                                    <TextField
                                        select
                                        label={t('RegionalUnit')}
                                        value={draftFilters.regionalUnitName}
                                        onChange={(event) =>
                                            setDraftFilters((current) => ({
                                                ...current,
                                                regionalUnitName: event.target.value,
                                                municipalityName: ''
                                            }))
                                        }
                                        fullWidth
                                    >
                                        {regionalUnits.map((regionalUnit) => (
                                            <MenuItem key={regionalUnit.name} value={regionalUnit.name}>
                                                {regionalUnit.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            )}
                            {municipalities.length > 0 && (
                                <Box>
                                    <TextField
                                        select
                                        label={t('Municipality')}
                                        value={draftFilters.municipalityName}
                                        onChange={(event) =>
                                            setDraftFilters((current) => ({ ...current, municipalityName: event.target.value }))
                                        }
                                        fullWidth
                                    >
                                        {municipalities.map((municipality) => (
                                            <MenuItem key={municipality.name} value={municipality.name}>
                                                {municipality.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClearFilters}>{t('Clear')}</Button>
                    <Button onClick={() => setIsFilterOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={handleApplyFilters}>
                        {t('Apply')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default View;
